import { TextInput } from "app/components/FormInputs/FormInputs";
import { PASSWORD_VALIDATIONS } from "app/helpers/passwordHelper";
import { PasswordInput } from "app/screens/ProfileScreen/screens/SecurityScreen/components/PasswordInput";
import React, { lazy, Suspense, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import "./AuthScreenModal.scss";
const LoadingSpinner: React.LazyExoticComponent<any> = lazy(
  () => import("app/components/LoadingSpinner/LoadingSpinner")
);
const Modal: React.LazyExoticComponent<any> = lazy(
  () => import("app/components/Modal/Modal")
);
type Props = {
  resetPasswordActive: boolean;
  I18n: any;
  closeModal: (...args: Array<any>) => any;
  modalCancelText: string;
  reset: boolean;
  passwordResetResponse: any;
  resetPassword: (...args: Array<any>) => any;
  handleChange: (...args: Array<any>) => any;
  passwordResetLoading: boolean;
  passwordResetError: string;
  submitNewPassword: (...args: Array<any>) => any;
  resetNewPassword: string;
  confirmPassword: string;
};

const AuthScreenModal: React.FC<Props> = props => {
  const {
    resetPasswordActive,
    I18n,
    closeModal,
    modalCancelText,
    reset,
    passwordResetResponse,
    resetPassword,
    handleChange,
    passwordResetLoading,
    passwordResetError,
    submitNewPassword,
    resetNewPassword,
    confirmPassword,
  } = props;
  const intl = useIntl();
  const [hasValidationError, setHasValidationError] = useState(false);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    for (const { regex, messageId } of Object.values(PASSWORD_VALIDATIONS)) {
      if (!regex.test(password)) {
        errors.push(intl.formatMessage({ id: messageId }));
      }
    }
    return errors;
  };

  const validateConfirmPassword = (confirmValue: string): string[] => {
    const confirmErrors: string[] = [];
    if (resetNewPassword !== confirmValue) {
      confirmErrors.push(
        intl.formatMessage({ id: "password-validation-message-match" })
      );
    }
    return confirmErrors;
  };

  const handleInput = (id: string, value: string) => {
    handleChange(id, value);
    let newErrors = { ...errors };

    if (id === "resetNewPassword") {
      newErrors = {
        ...newErrors,
        newPassword: validatePassword(value),
      };
    }
    if (id === "resetPasswordConfirmation") {
      newErrors = {
        ...newErrors,
        retypePassword: validateConfirmPassword(value),
      };
    }

    setErrors(newErrors);
    setHasValidationError(
      newErrors.newPassword.length > 0 || newErrors.retypePassword.length > 0
    );
  };

  const [errors, setErrors] = useState({
    newPassword: [] as string[],
    retypePassword: [] as string[],
    submit: false,
  });

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const id = event.target.id;
    if (id === "resetNewPassword") {
      setErrors(prev => ({
        ...prev,
        newPassword: validatePassword(resetNewPassword),
      }));
    }
    if (id === "resetPasswordConfirmation") {
      setErrors(prev => ({
        ...prev,
        retypePassword: validateConfirmPassword(confirmPassword),
      }));
    }
  };

  return (
    <Modal
      modalOpen={resetPasswordActive}
      closeModal={closeModal}
      contentLabel="Password Reset"
      cancelButton
      cancelButtonText={modalCancelText}
      className="auth-screen__password-modal"
    >
      {passwordResetResponse ? (
        <div className="auth-screen__password-modal__response">
          {passwordResetResponse}
        </div>
      ) : (
        <form onSubmit={resetPassword}>
          <h1>
            <FormattedMessage tagName="span" id="home-passwordreset" />
          </h1>
          {passwordResetLoading ? (
            <Suspense fallback={<div className="fallback">loading...</div>}>
              <LoadingSpinner />
            </Suspense>
          ) : (
            <TextInput
              type="text"
              id="username"
              className="auth-screen__form__input"
              removeDefaultStyle={true}
              label={I18n.username}
              onChange={handleChange}
            />
          )}
          {/* if setting a new password show 2 password inputs */}
          {reset && (
            <PasswordInput
              id="resetNewPassword"
              value={resetNewPassword}
              onChange={handleInput}
              label={I18n.newPassword}
              placeholder={I18n.newPasswordPlaceholder}
              errors={errors.newPassword}
              onBlur={handleBlur}
              showValidationMessage
            />
          )}
          {reset && (
            <PasswordInput
              id="resetPasswordConfirmation"
              value={confirmPassword}
              onChange={handleInput}
              label={I18n.retypepassword}
              placeholder={I18n.retypePasswordPlaceholder}
              errors={errors.retypePassword}
              onBlur={handleBlur}
            />
          )}
          <span className="error-msg">{passwordResetError}</span>
          <div className="auth-screen__form__submit">
            {/* if user not resetting password display login button */}
            {!reset ? (
              <button
                id="login_button"
                type="submit"
                disabled={passwordResetLoading}
              >
                <FormattedMessage tagName="span" id="button-submit" />
              </button>
            ) : (
              <button
                id="submit-new-password"
                type="button"
                disabled={passwordResetLoading || hasValidationError}
                onClick={submitNewPassword}
              >
                <FormattedMessage tagName="span" id="button-submit" />
              </button>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
};

export default AuthScreenModal;
